import { useTranslation } from 'react-i18next';
import { Mail, Phone, MessageCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { contact, hasLine, hasPhone, hasEmail, telHref } from '../config/contact';
import { trackContactClick } from '../lib/analytics';

interface ContactChannelsProps {
  /** card = large tiles (contact page), row = compact inline links (footer) */
  variant?: 'card' | 'row';
}

interface Channel {
  key: 'line' | 'phone' | 'email';
  enabled: boolean;
  href: string;
  external?: boolean;
  icon: LucideIcon;
  /** i18n key for the card heading; falls back to `fallbackLabel` when absent */
  labelKey?: string;
  fallbackLabel: string;
  value: string;
  /** Full tailwind classes — written out so the JIT scanner can see them. */
  iconClass: string;
  tileClass: string;
}

/**
 * Direct contact channels from src/config/contact.json.
 * Channels left blank in the config are hidden, so the component can ship
 * before every channel (e.g. LINE OA) is registered.
 */
export function ContactChannels({ variant = 'row' }: ContactChannelsProps) {
  const { t } = useTranslation('contact');

  const allChannels: Channel[] = [
    {
      key: 'phone',
      enabled: hasPhone,
      href: `tel:${telHref}`,
      icon: Phone,
      labelKey: 'contactInfo.callUs',
      fallbackLabel: 'Phone',
      value: contact.phone,
      iconClass: 'w-6 h-6 text-neon-green',
      tileClass: 'flex-shrink-0 w-12 h-12 rounded-lg bg-neon-green/10 flex items-center justify-center',
    },
    {
      key: 'email',
      enabled: hasEmail,
      href: `mailto:${contact.email}`,
      icon: Mail,
      labelKey: 'contactInfo.emailUs',
      fallbackLabel: 'Email',
      value: contact.email,
      iconClass: 'w-6 h-6 text-neon-cyan',
      tileClass: 'flex-shrink-0 w-12 h-12 rounded-lg bg-neon-cyan/10 flex items-center justify-center',
    },
    {
      key: 'line',
      enabled: hasLine,
      href: contact.lineUrl,
      external: true,
      icon: MessageCircle,
      fallbackLabel: 'LINE',
      value: contact.lineId,
      iconClass: 'w-6 h-6 text-neon-purple',
      tileClass: 'flex-shrink-0 w-12 h-12 rounded-lg bg-neon-purple/10 flex items-center justify-center',
    },
  ];

  const channels = allChannels.filter((c) => c.enabled);

  const externalProps = (c: Channel) =>
    c.external ? { target: '_blank', rel: 'noopener noreferrer' } : {};

  if (variant === 'row') {
    return (
      <div className="flex flex-col gap-2">
        {channels.map((c) => {
          const Icon = c.icon;
          return (
            <a
              key={c.key}
              href={c.href}
              {...externalProps(c)}
              onClick={() => trackContactClick(c.key, 'footer')}
              className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-neon-cyan transition-colors"
            >
              <Icon className="w-4 h-4" />
              {c.key === 'line' ? `LINE ${c.value}` : c.value}
            </a>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {channels.map((c) => {
        const Icon = c.icon;
        return (
          <a
            key={c.key}
            href={c.href}
            {...externalProps(c)}
            onClick={() => trackContactClick(c.key, 'contact_page')}
            className="glass-card rounded-xl p-6 hover:bg-white/[0.07] transition-colors text-left"
          >
            <div className="flex items-start gap-4">
              <div className={c.tileClass}>
                <Icon className={c.iconClass} />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-text-primary mb-1 font-heading">
                  {c.labelKey ? t(c.labelKey) : c.fallbackLabel}
                </h3>
                <p className="text-text-secondary text-sm whitespace-nowrap">{c.value}</p>
              </div>
            </div>
          </a>
        );
      })}
    </div>
  );
}
